// SCRUM-1971 — global passkey-adoption banner. Mounted once in the app shell so
// it survives the post-login navigation and can appear over any page. It renders
// whatever PasskeyPromptService pushes (the post-password enrolment nudge, or the
// cross-device "add a passkey to this device" offer) and routes the two actions
// back into the shared service. Dismissible; never blocks the app.
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PasskeyPromptService } from '../services/passkey-prompt.service';

@Component({
  selector: 'app-passkey-prompt',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="pk-prompt"
      *ngIf="(prompt$ | async) as prompt"
      data-testid="passkey-prompt"
      [attr.data-kind]="prompt.kind"
      role="dialog"
      aria-live="polite"
    >
      <div class="pk-prompt-body">
        <i class="pi pi-key pk-prompt-icon"></i>
        <div class="pk-prompt-text">
          <div class="pk-prompt-title">{{ prompt.title }}</div>
          <div class="pk-prompt-sub">{{ prompt.body }}</div>
        </div>
      </div>
      <div class="pk-prompt-actions">
        <button
          type="button"
          class="pk-prompt-later"
          data-testid="passkey-prompt-dismiss"
          [disabled]="working"
          (click)="dismiss()"
        >
          Not now
        </button>
        <button
          type="button"
          class="pk-prompt-cta"
          data-testid="passkey-prompt-accept"
          [disabled]="working"
          (click)="accept()"
        >
          {{ working ? 'Waiting for your device…' : prompt.cta }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .pk-prompt {
      position: fixed;
      z-index: 1200;
      left: 50%;
      bottom: 1.25rem;
      transform: translateX(-50%);
      width: min(calc(100vw - 2rem), 30rem);
      display: flex;
      flex-direction: column;
      gap: .75rem;
      padding: 1rem 1.25rem;
      border-radius: 12px;
      background: var(--surface-0, #ffffff);
      border: 1px solid var(--surface-border, #e2e8f0);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
    }
    .pk-prompt-body { display: flex; align-items: flex-start; gap: .75rem; }
    .pk-prompt-icon {
      font-size: 1.1rem;
      color: var(--primary-color, #0B6A43);
      margin-top: .15rem;
      flex-shrink: 0;
    }
    .pk-prompt-title { font-weight: 600; color: var(--text-color, #1e293b); line-height: 1.3; }
    .pk-prompt-sub { font-size: .85rem; color: var(--text-color-secondary, #64748b); line-height: 1.4; margin-top: .15rem; }
    .pk-prompt-actions { display: flex; justify-content: flex-end; gap: .5rem; }
    .pk-prompt-later {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--text-color-secondary, #64748b);
      font-weight: 600;
      padding: .5rem .75rem;
      border-radius: 8px;
    }
    .pk-prompt-later:hover:not(:disabled) { color: var(--text-color, #374151); }
    .pk-prompt-cta {
      background: var(--primary-color, #0B6A43);
      color: #ffffff;
      border: none;
      cursor: pointer;
      font-weight: 600;
      padding: .5rem 1rem;
      border-radius: 8px;
    }
    .pk-prompt-cta:hover:not(:disabled) { background: var(--primary-700, #085232); }
    .pk-prompt-cta:disabled, .pk-prompt-later:disabled { opacity: .65; cursor: not-allowed; }
  `],
})
export class PasskeyPromptComponent {
  readonly prompt$ = this.svc.prompt$;
  working = false;

  constructor(
    private readonly svc: PasskeyPromptService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  async accept(): Promise<void> {
    this.working = true;
    this.cdr.markForCheck();
    try {
      await this.svc.accept();
    } catch {
      // A dismissed device prompt or a ceremony error — just hide the banner
      // (without snoozing) so the user can be re-offered later. The password and
      // existing passkey flows are untouched.
      this.svc.clear();
    } finally {
      this.working = false;
      this.cdr.markForCheck();
    }
  }

  dismiss(): void {
    this.svc.dismiss();
  }
}
